// Pogadanka bota (krótki wstęp do etapu lekcji): maskotka po lewej, po prawej duży nagłówek (krótkie linijki, np. „Time for new words!”) albo dymek (dłuższe).
import { stripTags } from '../lib/text.ts';
import { config } from '../config.ts';
import { lineIn } from '../lib/teacher.ts';
import type { BanterTask } from '../types.ts';
import type { TaskEnv, TaskView } from './ctx.ts';
import { button, h } from './dom.ts';
import { tr } from './i18n.ts';
import { icon, mascot, stepPose } from './kit.ts';
import { L } from './taskText.ts';

/** Linijki do tylu znaków pokazujemy jako duży nagłówek, dłuższe w dymku. */
export const HEADING_MAX = 24;

/** Nagłówek z akcentem na końcu: „Time for | new words!” (ostatnie dwa słowa, a w krótszych — ostatnie). */
export function splitAccent(text: string): [string, string] {
  const words = text.trim().split(/\s+/);
  if (words.length < 2) return ['', text.trim()];
  const k = words.length >= 4 ? 2 : 1;
  return [words.slice(0, -k).join(' '), words.slice(-k).join(' ')];
}

export function renderBanter(task: BanterTask, env: TaskEnv): TaskView {
  const { line } = task;
  const { ctx } = env;
  const { player, teacher } = ctx;
  const langNow = () => teacher?.lang() ?? config.teacher.defaultLang;
  // po polsku (ustawienie języka uwag bota) gra i pokazuje się polska wersja linijki; angielską ma wtedy przycisk „Po polsku” tylko jako tłumaczenie
  let spoken = lineIn(line, langNow());
  const play = () => player.play(spoken.id, { text: spoken.text, lang: spoken.lang });

  const el = h('section', { class: 'task banter screen', 'data-task': 'banter', 'data-testid': 'task-banter' });
  // Zawartość składamy od nowa po każdej zmianie języka: polska wersja bywa dłuższa, więc może przejść z dużego nagłówka do dymku (i inna poza maskotki).
  // Napisy przycisków (Listen / In Polish / Next) są tu zwykłym tekstem w bieżącym języku (`tr`): każda zmiana EN/PL i tak przebudowuje całą zawartość.
  const fill = (): void => {
    const text = stripTags(spoken.text);
    const pl = line.pl && line.lang === 'en' && spoken.lang === 'en' ? line.pl : null;
    const plBox = h('p', { class: 'pl', hidden: true, 'data-testid': 'pl' }, pl);
    // „Po polsku”: pokazuje tłumaczenie pod zdaniem ORAZ czyta je głosem ElevenLabs (nagranie b.<id>.pl; bez pliku — polski głos przeglądarki); ponowne kliknięcie chowa napis i ucisza
    const polish = lineIn(line, 'pl');
    const showPl = pl ? button([h('span', { class: 'flag', 'aria-hidden': 'true' }, '🇵🇱'), tr(ctx, L.inPolish)], () => {
      plBox.hidden = !plBox.hidden;
      if (plBox.hidden) player.stop();
      else void player.play(polish.id, { text: stripTags(polish.text), lang: 'pl' });
    }, { class: 'btn ghost', testid: 'show-pl' }) : null;

    const heading = text.length <= HEADING_MAX;
    let say: HTMLElement;
    if (heading) {
      const [lead, accent] = splitAccent(text);
      say = h('h2', { class: 'head', 'data-testid': 'bubble' }, lead ? `${lead} ` : '', lead ? h('span', { class: 'accent' }, accent) : accent); // spacja zostaje w tekście (czytniki ekranu, kopiowanie), choć akcent jest w osobnym wierszu
    } else say = h('div', { class: 'bubble', 'data-testid': 'bubble' }, text);

    el.className = `task banter screen ${heading ? 'is-heading' : 'is-bubble'}`;
    el.setAttribute('data-lang', spoken.lang);
    el.replaceChildren(
      h('div', { class: 'screen-main' }, h('div', { class: 'banter-grid' },
        h('div', { class: 'mascot-stage' }, mascot(stepPose(line.step) ?? (heading ? 'idle' : 'calm'))),
        h('div', { class: 'col' }, say, plBox),
      )),
      h('div', { class: `actions-bar${showPl ? '' : ' two'}` },
        button([icon('speaker'), tr(ctx, L.listen)], () => void play(), { class: 'btn ghost', testid: 'listen' }),
        showPl,
        button([tr(ctx, L.next), icon('arrow')], () => env.done(null), { class: 'btn primary', testid: 'next' }),
      ),
    );
  };
  fill();

  // zmiana języka w trakcie pogadanki: od razu pokazujemy i czytamy linijkę w nowym języku (głosem ElevenLabs, nagranie b.<id>.pl)
  const off = teacher?.onChange(() => {
    player.stop();
    spoken = lineIn(line, langNow());
    fill();
    void play();
  });
  void play();
  return { el, destroy: () => off?.() };
}
